import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import { type LocationColorId } from '../../constants/locationColors';
import { LocationColorLine } from '../LocationSelector/LocationColorLine';
import {
  LOCATION_SEGMENTS,
  YEAR_QUERY_PARAM,
  locationPath
} from '../../routes/routeConstants';
import { hasTarget } from '../../services/overviewMetricsService';
import type { OverviewLocation } from '../../types/overviewTypes';
import { LocationCardBody } from './LocationCardBody';
import styles from './OverviewLocationCard.module.css';

interface OverviewLocationCardProps {
  locationId: string;
  name: string;
  colorId: LocationColorId;
  year: number;
  location: OverviewLocation | undefined;
}

export const OverviewLocationCard = ({
  locationId,
  name,
  colorId,
  year,
  location
}: OverviewLocationCardProps) => {
  const { t } = useTranslation();

  // The selected year travels into the year plan, so a planner reviewing 2025
  // and then opening a location stays in 2025 rather than being dropped into
  // today.
  const yearPlanPath = `${locationPath(locationId, LOCATION_SEGMENTS.yearOverview)}?${YEAR_QUERY_PARAM}=${year}`;
  // Only offered when the figures actually came back and actually lack a target.
  // With nothing known about the location, "set the target" would be advice
  // based on a failed request — the target may well already be set.
  const isTargetPromptShown = location !== undefined && !hasTarget(location.money);

  return (
    <article className={styles.card}>
      <header className={styles.header}>
        <LocationColorLine colorId={colorId} variant="block" />
        <h2 className={styles.name}>{name}</h2>
        <span className={styles.year}>{year}</span>
      </header>

      <LocationCardBody location={location} year={year} />

      <footer className={styles.footer}>
        {isTargetPromptShown && (
          <Link to={yearPlanPath} className={styles.secondaryLink}>
            {t('overview-set-target')}
          </Link>
        )}
        <Link to={yearPlanPath} className={styles.link}>
          {t('go-to-center')} <ChevronIcon className={styles.chevron} />
        </Link>
      </footer>
    </article>
  );
};
